import { useContext, useRef, useState, useCallback, useEffect } from 'react';

import { MAPBOX_TOKEN } from '../Utilities/Config';
import { mapContext } from '../Services/MapContext';

import '../Styles/MapControls.css';

function Controls(props) {
	const { onToggleInfo, language, onMobile } = props;
	const { map, layersLoaded, mapContainer  } = useContext(mapContext);

	const searchFocused = useRef(null);
	const [searchbarValue, setSearchbarValue] = useState('');
	const [searchbarMenuOpen, setSearchbarMenuOpen] = useState(false);
	const searchbarSuggestionsRef = useRef(null);
	const [searchbarSuggestions, setSearchbarSuggestions] = useState([]);
	const [loadingResults, setLoadingResults] = useState(false);

	// Mobile menu
	const mobileMenuRef = useRef();
	const mobileButtonRef = useRef();
	const [clickMobileMenu, setClickMobileMenu] = useState(false);
	const [showMobileMenu, setShowMobileMenu] = useState(false);
	const handleClickMobileMenu = (e) => {
		e.stopPropagation();
		e.preventDefault();
		setClickMobileMenu((prevState) => !prevState);
	}
	const handleClickOutside = (e) => {
		if (!mobileMenuRef.current.contains(e.target) && e.target !== mobileButtonRef.current) {
			setClickMobileMenu(false);
		}
	};
	useEffect(() => {
		setShowMobileMenu(clickMobileMenu);
	}, [clickMobileMenu]);
	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	});

	const clickZoomIn = (e) => {
		let currentZoom = map.getZoom();
		map.flyTo({zoom: currentZoom+1})
	}
	const clickZoomOut = (e) => {
		let currentZoom = map.getZoom();
		map.flyTo({zoom: currentZoom-1})
	}

	const onSearchbarChange = async (e) => {
		const rawValue = e.target.value;
		const trimmedValue = e.target.value.trim();
		setSearchbarValue(rawValue);
		if (trimmedValue.length > 1) {
			try {
				const endpoint = `https://api.mapbox.com/search/geocode/v6/forward?q=${rawValue}&access_token=${process.env.REACT_APP_MAPBOX_KEY || MAPBOX_TOKEN}&autocomplete=true&limit=7&country=dk&language=da`;
				const response = await fetch(endpoint);
				const results = await response.json();
				setSearchbarSuggestions(results?.features);
			} catch (error) {
				console.log("Error fetching data from mapbox: ", error);
			}
		}
		if (trimmedValue === '') setSearchbarSuggestions([]);
	}

	const onInputClickOut = (e) => {
		if (e.relatedTarget) {
			if (searchbarSuggestionsRef.current) {
				if (searchbarSuggestionsRef.current.contains(e.relatedTarget) || searchbarSuggestionsRef.current === e.relatedTarget) {
					// console.log('clicked on suggestions div');
				} else {
					setSearchbarMenuOpen(false);
				}
			} else {
				setSearchbarMenuOpen(false);
			}
		}
	}

	const onSuggestionClick = useCallback(async(suggestion) => {
		setLoadingResults(true);

		// hide previous popup
		for (let el of document.querySelectorAll('.mapboxgl-popup')) { el.remove() };

		const coords = suggestion.geometry.coordinates;
		const address = suggestion.properties.full_address;

		// Show sidebar or popup
		map.once('moveend', (e) => {
			if (e.originalEvent) return;
			// wait for features to load and click on the map
			map.once('idle', (e) => {
				const canvasCoordinates = map.project(coords);
				const clickEvent = new MouseEvent('click', {
					clientX: canvasCoordinates.x,
					clientY: canvasCoordinates.y,
					bubbles: true
				});
				mapContainer.current.querySelector('canvas').dispatchEvent(clickEvent);
				setLoadingResults(false);
			});
		});

		map.jumpTo({
			zoom: 9,
			center: coords
		});

		setSearchbarValue(address);
		setSearchbarMenuOpen(false);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [map, layersLoaded]);

	const handleClearSearchbar = () => {
		setSearchbarValue('');
		setSearchbarMenuOpen(false);
	}

	const focusOnSuggestions = (e) => {
		if (e.keyCode === 27) { // Esc
			// setSearchbarValue('');
			setSearchbarMenuOpen(false);
			if (searchFocused.current) {
				searchFocused.current?.blur();
			}
		}
		if (e.keyCode === 40) { // Down
			if (searchbarSuggestionsRef.current) {
				searchbarSuggestionsRef.current?.firstChild?.focus();
			}
		}
	}

	const interactWithSuggestions = (e, suggestion) => {
		// User pressed Enter
		if (e.key === 'Enter') {
			onSuggestionClick(suggestion);
		}
		// User pressed Esc
		if (e.keyCode === 27) {
			if (searchFocused.current) {
				searchFocused.current?.focus();
			}
		}
		// User pressed Up/Down cursor
		if (e.keyCode === 38 && e.target?.previousSibling) e.target.previousSibling.focus(); // Up
		if (e.keyCode === 40 && e.target?.nextSibling) e.target.nextSibling.focus(); // Down
	}

	const onSuggestionsBlur = (e) => {
		if (e.relatedTarget && e.target) {
			if (e.relatedTarget !== e.target) {
				if (searchbarSuggestionsRef.current) {
					if (!searchbarSuggestionsRef.current.contains(e.relatedTarget)) {
						setSearchbarMenuOpen(false);
					}
				}
			}
		}
	}

	const hasMobileMenu = (onMobile || window.innerWidth <= 900);

	return (
		<>
			<div className="controls-left" aria-label={language ? language['aria:mapcontrol:buttons:controls-left'] : ''}>
				<div className="searchbar-wrapper">
					<div className={`search-input-wrapper ${searchbarMenuOpen ? 'open' : ''}`}>
						<div className="search-btn" role="none" tabIndex="-1"></div>
						<input
							ref={searchFocused}
							className="search-input"
							type="text"
							placeholder={language ? language['map:search:placeholder'] : "Search for your place of residence"}
							aria-label={language ? language['map:search:placeholder'] : "Search for your place of residence"}
							data-form-type="other"
							data-dashlane-disabled-on-field="true"
							value={searchbarValue}
							onChange={onSearchbarChange}
							onFocus={() => setSearchbarMenuOpen(true)}
							onBlur={onInputClickOut}
							onKeyDown={focusOnSuggestions}
						/>
						{searchbarValue &&
							<div
								className="search-btn-clear"
								aria-label="Reset"
								tabIndex="1"
								onClick={handleClearSearchbar}
								onKeyDown={(e) => {if (e.key === 'Enter') handleClearSearchbar()}}
							></div>
						}
					</div>

					{searchbarMenuOpen && searchbarSuggestions.length > 0 && (
						<div
							className="searchbar-suggestions"
							ref={searchbarSuggestionsRef}
						>

							{loadingResults && <div className="searchbar-loading-wrapper">
								<div className="searchbar-loading"></div>
							</div>}

							{searchbarSuggestions.map((suggestion, index) => {
								return (
									<div
										key={index}
										tabIndex={0}
										className="suggestion"
										onClick={() => onSuggestionClick(suggestion)}
										onKeyDown={(e) => interactWithSuggestions(e, suggestion)}
										onBlur={onSuggestionsBlur}
									>
										{suggestion.properties.full_address}
									</div>
								);
							})}
						</div>
					)}
				</div>
			</div>

			<nav className="controls-top-right show" aria-label={language ? language['aria:mapcontrol:buttons:controls-top-right'] : ''}>
				{hasMobileMenu &&
					<button type="button" className="gp-button mobile-spoiler" onClick={handleClickMobileMenu} aria-label='Show more links'></button>
				}
				<div ref={mobileMenuRef} className={hasMobileMenu ? `mobile-wrapper ${showMobileMenu ? 'show': ''}` : 'desktop-wrapper'}>
					<a
						href={language ? language['map:buttons:take_action:url'] : 'https://www.greenpeace.org/denmark/vaer-med/rent-drikkevand/'}
						className="gp-button textbutton take-action"
						target="_blank"
						rel="noreferrer"
						aria-label="take action"
					>
						{language ? language['map:buttons:take_action:text'] : 'Tag handling'}
					</a>
					<a
						href={language ? language['map:buttons:read_more:url'] : 'https://www.greenpeace.org/denmark/'}
						className="gp-button textbutton read-more"
						target="_blank"
						rel="noreferrer"
						aria-label="read more"
					>
						{language ? language['map:buttons:read_more:text'] : 'Læs mere'}
					</a>
				</div>
			</nav>

			<div className="controls-right" aria-label={language ? language['aria:mapcontrol:buttons:controls-right'] : ''}>
				<div className="zoom-wrapper">
					<button type="button" className="gp-button zoom-in" onClick={clickZoomIn}></button>
					<button type="button" className="gp-button zoom-out" onClick={clickZoomOut}></button>
				</div>
				<button type="button" className="gp-button info" onClick={onToggleInfo}></button>
			</div>
		</>
	);
}

export default Controls;
